import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@core-ui/Components/ui/dialog';
import { Button } from '@core-ui/Components/ui/button';
import { Input } from '@core-ui/Components/ui/input';
import { cn } from '@core-ui-lib/utils';

import { Loader2 } from 'lucide-react';

import type { ContactInfoDto, UpdateContactDto } from '@features/contacts';

interface EditContactDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  contact: ContactInfoDto | null;
  isLoading: boolean;
  onSaveContact: (contactData: UpdateContactDto) => void;
}

interface ContactFormData {
  name: string;
  phone: string;
  accountName: string;
  jobTitle: string;
  email: string;
  notes: string;
  crmId: string;
}

interface FormErrors {
  name?: string;
  phone?: string;
  email?: string;
}

interface TouchedFields {
  name?: boolean;
  phone?: boolean;
  email?: boolean;
}

const EMPTY_CONTACT_FORM: ContactFormData = {
  name: '',
  phone: '',
  accountName: '',
  jobTitle: '',
  email: '',
  notes: '',
  crmId: '',
};

export default function EditContactDialog ({
  isOpen,
  onOpenChange,
  contact,
  isLoading,
  onSaveContact,
}: EditContactDialogProps): React.JSX.Element {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<ContactFormData>(EMPTY_CONTACT_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<TouchedFields>({});

  const editContactSchema = z.object({
    name: z.string().min(1, t('contacts.editContactDialog.errors.nameRequired')),
    phone: z.string().min(1, t('contacts.editContactDialog.errors.phoneRequired')),
    email: z.string().email(t('contacts.editContactDialog.errors.emailInvalid')).or(z.literal('')),
  });

  useEffect(() => {
    if (contact !== null) {
      setFormData({
        name: contact.name ?? '',
        phone: contact.phone ?? '',
        accountName: contact.accountName ?? '',
        jobTitle: contact.jobTitle ?? '',
        email: contact.email ?? '',
        notes: contact.notes ?? '',
        crmId: contact.crmId ?? '',
      });
    } else {
      setFormData(EMPTY_CONTACT_FORM);
    }
    setErrors({});
    setTouched({});
  }, [contact]);

  const validateField = useCallback(
    (field: 'name' | 'phone' | 'email', value: string) => {
      let error: string | undefined;

      if (field === 'name') {
        const result = z.string().min(1, t('contacts.editContactDialog.errors.nameRequired')).safeParse(value);
        error = result.success ? undefined : result.error.errors[0]?.message;
      } else if (field === 'phone') {
        const result = z.string().min(1, t('contacts.editContactDialog.errors.phoneRequired')).safeParse(value);
        error = result.success ? undefined : result.error.errors[0]?.message;
      } else if (field === 'email') {
        if (value.trim() === '') {
          error = undefined;
        } else {
          const result = z.string().email(t('contacts.editContactDialog.errors.emailInvalid')).safeParse(value);
          error = result.success ? undefined : result.error.errors[0]?.message;
        }
      }

      setErrors((prev) => ({ ...prev, [field]: error }));
    },
    [t],
  );

  const validate = useCallback((): boolean => {
    const result = editContactSchema.safeParse({
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
    });

    if (!result.success) {
      const fieldErrors: FormErrors = {};
      result.error.errors.forEach((e) => {
        const fieldName = e.path[0] as keyof FormErrors;
        if (fieldName !== undefined) {
          fieldErrors[fieldName] = e.message;
        }
      });
      setErrors(fieldErrors);
      setTouched({ name: true, phone: true, email: true });
      return false;
    }
    setErrors({});
    return true;
  }, [formData.name, formData.phone, formData.email, t]);

  const handleSaveContact = (): void => {
    if (contact === null || !validate()) {
      return;
    }

    const trimmedAccountName = formData.accountName.trim();
    const trimmedJobTitle = formData.jobTitle.trim();
    const trimmedEmail = formData.email.trim();
    const trimmedNotes = formData.notes.trim();
    const trimmedCrmId = formData.crmId.trim();

    const contactUpdateData: UpdateContactDto = {
      id: contact.id,
      name: formData.name.trim(),
      phone: formData.phone.trim(),
      accountName: trimmedAccountName !== '' ? trimmedAccountName : undefined,
      jobTitle: trimmedJobTitle !== '' ? trimmedJobTitle : undefined,
      email: trimmedEmail !== '' ? trimmedEmail : undefined,
      notes: trimmedNotes !== '' ? trimmedNotes : undefined,
      crmId: trimmedCrmId !== '' ? trimmedCrmId : undefined,
    };

    onSaveContact(contactUpdateData);
  };

  const handleFieldChange = (field: keyof ContactFormData, value: string): void => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'name' || field === 'phone' || field === 'email') {
      if (touched[field] === true) {
        validateField(field, value);
      }
    }
  };

  const handleFieldBlur = (field: 'name' | 'phone' | 'email'): void => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field, formData[field]);
  };

  const canSaveContact =
    formData.name.trim() !== '' &&
    formData.phone.trim() !== '' &&
    errors.name === undefined &&
    errors.phone === undefined &&
    errors.email === undefined &&
    !isLoading;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[620px] bg-card border-border text-card-foreground rounded-md p-4 md:p-6 shadow">
        <DialogHeader>
          <DialogTitle>{t('contacts.editContactDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('contacts.editContactDialog.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <div className="text-sm font-medium">{t('contacts.editContactDialog.nameLabel')}</div>
            <Input
              value={formData.name}
              onChange={(event) => {
                handleFieldChange('name', event.target.value);
              }}
              onBlur={() => {
                handleFieldBlur('name');
              }}
              placeholder={t('contacts.editContactDialog.namePh')}
              className={cn(
                touched.name === true && errors.name !== undefined &&
                '!border-error !ring-0 focus:!ring-error focus:!border-error',
              )}
            />
            {touched.name === true && errors.name !== undefined && (
              <p className="text-xs text-error">{errors.name}</p>
            )}
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium">{t('contacts.editContactDialog.phoneLabel')}</div>
            <Input
              value={formData.phone}
              onChange={(event) => {
                handleFieldChange('phone', event.target.value);
              }}
              onBlur={() => {
                handleFieldBlur('phone');
              }}
              placeholder={t('contacts.editContactDialog.phonePh')}
              className={cn(
                touched.phone === true && errors.phone !== undefined &&
                '!border-error !ring-0 focus:!ring-error focus:!border-error',
              )}
            />
            {touched.phone === true && errors.phone !== undefined && (
              <p className="text-xs text-error">{errors.phone}</p>
            )}
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium">{t('contacts.editContactDialog.companyLabel')}</div>
            <Input
              value={formData.accountName}
              onChange={(event) => {
                handleFieldChange('accountName', event.target.value);
              }}
              placeholder={t('contacts.editContactDialog.companyPh')}
            />
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium">{t('contacts.editContactDialog.positionLabel')}</div>
            <Input
              value={formData.jobTitle}
              onChange={(event) => {
                handleFieldChange('jobTitle', event.target.value);
              }}
              placeholder={t('contacts.editContactDialog.positionPh')}
            />
          </div>

          <div className="space-y-2 md:col-span-2">
            <div className="text-sm font-medium">{t('contacts.editContactDialog.emailLabel')}</div>
            <Input
              value={formData.email}
              onChange={(event) => {
                handleFieldChange('email', event.target.value);
              }}
              onBlur={() => {
                handleFieldBlur('email');
              }}
              placeholder={t('contacts.editContactDialog.emailPh')}
              className={cn(
                touched.email === true && errors.email !== undefined &&
                '!border-error !ring-0 focus:!ring-error focus:!border-error',
              )}
            />
            {touched.email === true && errors.email !== undefined && (
              <p className="text-xs text-error">{errors.email}</p>
            )}
          </div>

          <div className="space-y-2 md:col-span-2">
            <div className="text-sm font-medium">{t('contacts.editContactDialog.notesLabel')}</div>
            <Input
              value={formData.notes}
              onChange={(event) => {
                handleFieldChange('notes', event.target.value);
              }}
              placeholder={t('contacts.editContactDialog.notesPh')}
            />
          </div>

          <div className="space-y-2 md:col-span-2">
            <div className="text-sm font-medium">{t('contacts.editContactDialog.crmIdLabel')}</div>
            <Input
              value={formData.crmId}
              onChange={(event) => {
                handleFieldChange('crmId', event.target.value);
              }}
              placeholder={t('contacts.editContactDialog.crmIdPh')}
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            className="border border-border text-muted-foreground hover:bg-muted"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {t('contacts.editContactDialog.cancel')}
          </Button>
          <Button
            onClick={handleSaveContact}
            disabled={!canSaveContact}
            className="gap-2"
          >
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('contacts.editContactDialog.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
